import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, MapPin, Navigation } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader, EmptyState, UserAvatar, SpecPill, AvailabilityPill, Pill, VipBadge } from "@/components/common";
import { useApi, errMsg } from "@/lib/api";
import { SPECS } from "@/lib/labels";

export const RADII = [25, 50, 100, 250, 500];

export function PersonCard({ p, index = 0, right }) {
  return (
    <div data-testid={`person-card-${p.user_id}`} style={{ animationDelay: `${index * 40}ms` }} className="rise panel p-5 flex gap-4 hover:border-sky-400 transition-colors">
      <Link to={`/u/${p.user_id}`}><UserAvatar user={p} size={52} /></Link>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/u/${p.user_id}`} className="font-bold text-slate-900 hover:text-sky-700" data-testid={`person-link-${p.user_id}`}>{p.name}</Link>
          <VipBadge user={p} />
        </div>
        <p className="text-sm text-slate-600 truncate">{p.role === "company" ? "Company" : p.headline || "Technician"}</p>
        {p.location && (
          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin size={12} />{p.location}
            {p.distance_km != null && <span className="ml-1 font-mono text-sky-700" data-testid={`person-distance-${p.user_id}`}>· {p.distance_km} km</span>}
          </p>
        )}
        <div className="mt-2 flex flex-wrap gap-1.5">
          {p.role === "company" && <Pill cls="bg-slate-900 text-amber-400 border-slate-900">Company</Pill>}
          {(p.specializations || []).map((s) => <SpecPill key={s} spec={s} />)}
          <AvailabilityPill value={p.availability} />
        </div>
      </div>
      {right}
    </div>
  );
}

export function NearFilter({ near, setNear, radius, setRadius, prefix }) {
  return (
    <div className="flex gap-2 md:w-[340px]">
      <div className="relative flex-1">
        <Navigation size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid={`${prefix}-near-input`} className="pl-9 h-11 bg-white" placeholder="Near city (e.g. Sundsvall)" value={near} onChange={(e) => setNear(e.target.value)} />
      </div>
      <Select value={String(radius)} onValueChange={(v) => setRadius(Number(v))}>
        <SelectTrigger data-testid={`${prefix}-radius-select`} className="w-[110px] h-11 bg-white"><SelectValue /></SelectTrigger>
        <SelectContent>{RADII.map((r) => <SelectItem key={r} value={String(r)}>{r} km</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

export default function People() {
  const api = useApi();
  const [params] = useSearchParams();
  const [q, setQ] = useState("");
  const [role, setRole] = useState(params.get("role") || "all");
  const [spec, setSpec] = useState(params.get("specialization") || "all");
  const [near, setNear] = useState(params.get("near") || "");
  const [radius, setRadius] = useState(Number(params.get("radius")) || 100);
  const [people, setPeople] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (role !== "all") p.set("role", role);
    if (spec !== "all") p.set("specialization", spec);
    if (near.trim()) { p.set("near", near.trim()); p.set("radius_km", radius); }
    const t = setTimeout(() => api("get", `/users?${p}`).then((d) => { setPeople(d); setError(""); })
      .catch((e) => { setPeople([]); setError(errMsg(e)); }), near.trim() ? 900 : 300);
    return () => clearTimeout(t);
  }, [q, role, spec, near, radius, api]);

  return (
    <div>
      <p className="eyebrow">Community</p>
      <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight">Technicians & companies</h1>
      <div className="mt-8 flex flex-col xl:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="people-search-input" className="pl-9 h-11 bg-white" placeholder="Search: name, skill, turbine..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <NearFilter near={near} setNear={setNear} radius={radius} setRadius={setRadius} prefix="people" />
        <div className="flex gap-3 flex-wrap">
          <Tabs value={role} onValueChange={setRole}>
            <TabsList className="h-11">
              <TabsTrigger value="all" data-testid="people-tab-all">All</TabsTrigger>
              <TabsTrigger value="technician" data-testid="people-tab-technician">Technicians</TabsTrigger>
              <TabsTrigger value="company" data-testid="people-tab-company">Companies</TabsTrigger>
            </TabsList>
          </Tabs>
          <Select value={spec} onValueChange={setSpec}>
            <SelectTrigger data-testid="people-filter-specialization" className="md:w-52 h-11 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any specialization</SelectItem>
              {Object.entries(SPECS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-6">
        {people === null ? <Loader /> : people.length === 0 ? <EmptyState testId="people-empty" title={error || "No one found"} text={near && !error ? "Try a larger radius." : undefined} /> : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="people-list">
            {people.map((p, i) => <PersonCard key={p.user_id} p={p} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
